'use client';
// Réclamations (design isRecl — écoute patient, ISO 15189 §7.7).
// L'accusé de réception est tracé dès l'enregistrement.
import React, { useEffect, useMemo, useState } from 'react';
import { useOngletAdresse } from '@/lib/useAdresse';
import { peutDecloturerNC, peutModifierReclamation } from '@/lib/droits-nc';
import { LigneLien } from '@/shared/Tableau';
import { cheminDe } from '@/lib/adresse';
import { useGed, userName } from '@/lib/store';
import { REC_CANAUX, REC_STATES, NC_GRAVITES, ACTION_STATES, AC_BLOQUANTES, vocabMeta, graviteMeta, fmtDate, fmtDateTime, joursDepuis, SEUILS_DEFAUT } from '@/lib/meta';
import {
  TitrePage, I, card, inputStyle,
  Skeleton, EmptyState, FilterBar, MentionTri, FieldError, RequiredHint, useBusy, RefISO, Modale } from '@/components/ui';
import { Liens } from '@/components/Liens';
import { LienFiche } from '@/components/LienFiche';
import { BoutonJournal } from '@/components/BoutonJournal';
import { JournalFiche } from '@/components/JournalFiche';
import { OngletsDossier, PanneauDossier, BarreAction } from '@/components/Dossier';
import { ActionRedaction } from '@/components/ActionRedaction';
// La règle de clôture, écrite UNE fois pour la NC, l'audit et la réclamation.
import { BlocageCloture } from '@/components/Cloture';
import type { GedReclamation } from '@/lib/types';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { Norme } from '@/components/Norme';
import { useFormulaire } from '@/components/Formulaire';

import { Bouton } from '@/shared/Bouton';
import { DeclareForm } from '@/components/screens/NC';
import { ChoixCorrespondant, useNomsContacts } from '@/components/ChoixCorrespondant';
import { PiedActions } from '@/shared/PiedActions';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

/** Une réclamation non soldée. Source unique — le tableau de bord s'en sert
 *  aussi, pour ne pas diverger du registre. */
export const reclOuverte = (r: GedReclamation) => r.statut !== 'cloturee';

/** Délai au-delà duquel une réclamation sans réponse est signalée. Le §7.7 fait
 *  du traitement des réclamations un engagement de délai : sans repère à
 *  l'écran, rien ne distinguait une réclamation d'hier d'une de trois mois.
 *  Le délai retenu appartient au laboratoire — il vit dans le paramétrage. */
function useSeuilReponse() {
  const { settings } = useGed();
  const v = settings?.notif?.reclReponseJ;
  return typeof v === 'number' && v > 0 ? v : SEUILS_DEFAUT.reclReponseJ;
}

function Delai({ r }: { r: GedReclamation }) {
  const seuil = useSeuilReponse();
  if (r.statut === 'cloturee') return null;
  const j = joursDepuis(r.dateReception);
  if (j === null || j < 1) return null;
  const tard = j > seuil;
  return (
    <span title={tard ? `Sans clôture depuis plus de ${seuil} jours` : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: tard ? 'var(--red-50)' : 'var(--surface-sunken)', color: tard ? 'var(--red-600)' : 'var(--text-muted)' }}>
      en cours depuis {j} j
    </span>
  );
}

/** Masqué plutôt que démonté : un clic dans la liste ne doit pas effacer la saisie. */
function NewForm({ visible, onDone, onDirty }: { visible: boolean; onDone: () => void; onDirty: (d: boolean) => void }) {
  const { smq } = useGed();
  const vide = { canal: 'patient', gravite: 'mineure', plaignant: '', objet: '', description: '', correspondantCode: '' };
  const [f, setF] = useState(vide);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const modifie = JSON.stringify(f) !== JSON.stringify(vide);
  useEffect(() => { onDirty(modifie); }, [modifie, onDirty]);

  /* Contrôle au socle partagé — le rouge sous le champ ne se voit pas depuis
     le bas d'un formulaire à dix rubriques. */
  const declaration = React.useMemo(() => [
    { id: 'recl-objet', label: 'Objet de la réclamation', valeur: f.objet, requis: true },
  ], [f.objet]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const submit = () => run('creer', async () => {
    if (!verifier()) return;
    setErreur('');
    const ok = await smq('ged/recl', f, 'Réclamation enregistrée — accusé de réception tracé.', 'Enregistrement de la réclamation');
    if (ok) { setF(vide); onDone(); }
  });

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 20, display: visible ? 'block' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Enregistrer une réclamation</h2>
        <RequiredHint />
      </div>
      <Bandeau />
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>Un accusé de réception est tracé dès l’enregistrement.</p>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl}>Canal</label>
          <select value={f.canal} onChange={(e) => setF({ ...f, canal: e.target.value })} style={inputStyle}>
            {REC_CANAUX.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></div>
        <div><label style={lbl}>Gravité</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {NC_GRAVITES.map((g) => {
              const on = f.gravite === g.key; const m = graviteMeta(g.key);
              return <button key={g.key} onClick={() => setF({ ...f, gravite: g.key })} aria-pressed={on} style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer', border: `1.5px solid ${on ? m.fg : 'var(--border-default)'}`, background: on ? m.bg : 'var(--surface-card)', color: on ? m.fg : 'var(--text-muted)' }}>{g.label}</button>;
            })}
          </div></div>
        {/* LE CORRESPONDANT QUI RÉCLAME, relié au carnet (14/10/2026) — quand ce
            n'est pas un patient. Son nom remplit « Plaignant » s'il est vide. */}
        {f.canal !== 'patient' && (
          <div><label style={lbl} htmlFor="recl-corresp">Quel correspondant réclame ?</label>
            <ChoixCorrespondant id="recl-corresp" valeur={f.correspondantCode} onChange={(v) => setF({ ...f, correspondantCode: v })}
              aide="Relié à l’annuaire : la réclamation apparaîtra sur sa fiche." /></div>
        )}
        <div><label style={lbl}>Plaignant</label><input value={f.plaignant} onChange={(e) => setF({ ...f, plaignant: e.target.value })} placeholder="Ex. Dr K. Mejri — ou laisser le nom du correspondant" style={inputStyle} /></div>
        <div>
          <label style={lbl} htmlFor="recl-objet">Objet <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="recl-objet" value={f.objet} onChange={(e) => { setF({ ...f, objet: e.target.value }); if (erreur) setErreur(''); }}
            aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
          <FieldError>{erreur}</FieldError>
        </div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} /></div>
      </div>
      <PiedActions taille="sm" style={{ marginTop: 14 }} annuler={{ onClick: () => { setF(vide); setErreur(''); onDone(); } }} principale={{ libelle: 'Enregistrer et accuser réception', libelleChargement: 'Enregistrement…', chargement: estOccupe('creer'), disabled: !f.objet.trim() || estOccupe(), onClick: () => void submit() }} />
    </div>
  );
}

type OngletRecl = 'description' | 'reponse' | 'actions' | 'liens';
/* Les valeurs ADMISES dans l'adresse. Elle existe pour refuser
   `?onglet=nimportequoi` : sans elle, une adresse tronquée par un logiciel de
   messagerie ouvrirait un onglet vide au lieu du premier. */
const ONGLETS_ONGLETRECL: readonly OngletRecl[] = ['description', 'reponse', 'actions', 'liens'];

/**
 * Le DOSSIER d'une réclamation — exporté sous le nom `ReclamationDossier`.
 *
 * Exporté parce qu'il s'affiche désormais à deux endroits : ici, dans le
 * registre, et EN PANNEAU par-dessus la fiche depuis laquelle on l'a ouverte.
 * Le recopier aurait donné deux fiches réclamation qui divergent — même raison
 * que pour `ActionDossier`.
 *
 * `enPanneau` ne change rien au contenu : il retire le cadre de carte (le
 * panneau en fournit un, sans quoi carte dans une carte) et neutralise les
 * boutons qui QUITTERAIENT l'écran, puisque le panneau a son propre
 * « Ouvrir en pleine page ».
 */
export function ReclamationDossier({ r, enPanneau = false }: { r: GedReclamation; enPanneau?: boolean }) {
  return <Detail r={r} enPanneau={enPanneau} />;
}

/**
 * Déclôture d'une réclamation — réservée au RQ et au CQ, motivée, signée.
 *
 * ⚠️ ALIGNÉE SUR CELLE DES NC LE 08/10/2026 (arbitrage RQ) : les deux
 * déclôtures sont jumelles depuis le 14/08, et les laisser diverger aurait
 * donné deux dossiers scellés qui se rouvrent par deux droits différents —
 * donc deux journaux qu'un évaluateur ne peut pas comparer.
 *
 * ⚠️ C'est le SEUL acte du registre des réclamations ouvert au coordinateur
 * qualité : il ne mène pas ce dossier, il peut seulement en défaire le sceau.
 *
 * Une réclamation clôturée est un dossier SCELLÉ : c'est le premier endroit
 * qu'un évaluateur regarde. La rouvrir sans motif écrit se lirait, six mois
 * plus tard, comme une retouche du registre. Le motif n'est donc pas une
 * formalité — c'est lui qui distingue une reprise assumée d'une correction
 * discrète, et il part au journal signé (`ReclamationDecloturee`).
 *
 * La réclamation revient à « réponse émise » et non à « reçue » : la réponse a
 * bel et bien été émise, revenir en deçà effacerait un fait. Ce qu'on rouvre,
 * c'est la suite à donner.
 */
function Decloture({ recl }: { recl: GedReclamation }) {
  const { smq } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const { run, estOccupe } = useBusy();
  // 20, comme `MOTIF_DEROGATION_MIN` au serveur. Un minimum plus bas à l'écran
  // laisserait passer une saisie que le serveur refuse ensuite : le refus
  // arriverait après le clic, sur un dossier scellé — le pire moment.
  const MINI = 20;
  const assez = motif.trim().length >= MINI;

  return (
    <>
      <Bouton taille="sm" onClick={() => setOuvert(true)} title="Rouvrir ce dossier scellé. Réservé au responsable qualité et au coordinateur qualité, motif obligatoire, événement signé au journal.">
        Déclôturer la réclamation
      </Bouton>
      {ouvert && (
        <Modale
          titre={`Déclôturer ${recl.ref}`}
          sousTitre="Le dossier revient à « réponse émise ». L’événement est signé au journal."
          onClose={() => setOuvert(false)}
          largeur={560}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
              <Bouton variante="primaire" taille="sm" onClick={() => void run('decl', async () => {
                  const ok = await smq(`ged/recl/${recl.documentId}/status`,
                    { statut: 'reponse', motifDerogation: motif.trim() },
                    'Réclamation déclôturée — événement signé.');
                  if (ok) { setOuvert(false); setMotif(''); }
                })} disabled={!assez || estOccupe()}>
                {estOccupe('decl') ? 'Déclôture…' : 'Déclôturer'}
              </Bouton>
            </div>
          )}>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={lbl} htmlFor="decl-motif">Motif de la déclôture</label>
            <textarea id="decl-motif" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="Ce qui justifie de rouvrir un dossier scellé — le réclamant est revenu, l’action s’est révélée insuffisante…"
              style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
            {/* Un bouton grisé sans phrase se lit comme une panne : le refus
                est NOMMÉ, avec ce qui manque exactement. */}
            <FieldError>
              {assez ? '' : `Motif trop court : ${MINI} caractères au minimum (${motif.trim().length} saisi${motif.trim().length > 1 ? 's' : ''}).`}
            </FieldError>
          </div>
        </Modale>
      )}
    </>
  );
}

function Detail({ r, enPanneau = false }: { r: GedReclamation; enPanneau?: boolean }) {
  const nomsTiers = useNomsContacts(r.correspondantCode ? [r.correspondantCode] : []);
  const { smq, smqData, settings, session, events, loadJournal, signalerLecture, actions, go, ouvrirPanneau, liens } = useGed();
  const [rep, setRep] = useState(r.reponse);
  const [retour, setRetour] = useState(r.retourPlaignant || '');
  /* CE QUE L'ONGLET « RÉPONSE » A LU (14/10/2026), envoyé sous `base` : le
     moteur n'écrit que ce qui a changé ici. Sans elle, le CQ qui complétait le
     retour au réclamant renvoyait aussi la réponse lue à l'ouverture — et
     effaçait celle que la RQ venait de reformuler. Recalée sur ce qu'on vient
     d'écrire après chaque enregistrement (le formulaire reste ouvert). */
  const [baseRep, setBaseRep] = useState(() => ({ reponse: r.reponse || '', retourPlaignant: r.retourPlaignant || '' }));
  const enregistrerReponse = () => run('rep', async () => {
    const corps = { reponse: rep, retourPlaignant: retour };
    const ok = await smq(`ged/recl/${r.documentId}/reponse`, { ...corps, base: baseRep }, 'Réponse enregistrée.', `Réponse à ${r.ref}`);
    if (ok) setBaseRep(corps);
    return ok;
  });
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : il se partage par lien, F5 y
     revient, et « Précédent » ramène à l'onglet précédent au lieu de quitter
     l'écran. */
  const [onglet, setOnglet] = useOngletAdresse<OngletRecl>('description', ONGLETS_ONGLETRECL);
  // Action tout juste créée, à formuler sans quitter la fiche réclamation.
  const [acARemplir, setAcARemplir] = useState<string | null>(null);
  /** La déclaration d'une NC NÉE DE CETTE RÉCLAMATION est ouverte (13/10/2026). */
  const [declareNC, setDeclareNC] = useState(false);
  const { run, estOccupe } = useBusy();
  useEffect(() => { void loadJournal(r.ref); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [r.documentId, r.statut]);
  /* Idem NC : l'ouverture par le RQ ou son suppléant fait passer la réclamation
     en analyse, sans clic. `documentId` seul en dépendance — voir NC.tsx. */
  useEffect(() => { void signalerLecture('recl', r.documentId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [r.documentId]);
  const canManage = session.roles.includes('rq') || r.responsable === session.key;
  const journal = events[r.ref] || [];
  /* Le CQ rédige la réponse d'une réclamation (13/10/2026) — ET RIEN D'AUTRE :
     `canManage` garde les transitions pour le RQ et le responsable. */
  const peutRedigerReponse = canManage || peutModifierReclamation(session.roles);
  // Même critère que le serveur (origine + origineRef) : un écart ferait
  // diverger le blocage de clôture affiché et celui réellement appliqué.
  const linked = actions.filter((a) => a.origine === 'reclamation' && a.origineRef === r.ref);
  const nbLiens = liens.filter((l) => (l.sourceType === 'reclamation' && l.sourceRef === r.ref) || (l.cibleType === 'reclamation' && l.cibleRef === r.ref)).length;

  const transition = (statut: string, okToast: string) =>
    run(statut, () => smq(`ged/recl/${r.documentId}/status`, { statut }, okToast));

  return (
    <div style={enPanneau
      ? { padding: '4px 2px', display: 'grid', gap: 12 }
      : { ...card, padding: 20, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
        <LienFiche vue="recl" reference={r.ref} quoi="cette réclamation" />
        <BoutonJournal reference={r.ref} quoi="de cette réclamation" evenements={journal} />
        <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: vocabMeta(REC_STATES, r.statut).bg, color: vocabMeta(REC_STATES, r.statut).fg, fontSize: 11, fontWeight: 700 }}>{vocabMeta(REC_STATES, r.statut).label}</span>
        <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: graviteMeta(r.gravite).bg, color: graviteMeta(r.gravite).fg, fontSize: 11, fontWeight: 700 }}>{graviteMeta(r.gravite).label}</span>
        <Delai r={r} />
      </div>
      <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)' }}>{r.objet}</h2>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {REC_CANAUX.find((c) => c.key === r.canal)?.label} · {r.plaignant || '—'}{r.correspondantCode ? ` (${nomsTiers.get(r.correspondantCode)?.nom || r.correspondantCode})` : ''} · reçue le {fmtDate(r.dateReception)} ·
        <span style={{ color: 'var(--green-600)', fontWeight: 600 }}> accusé tracé le {fmtDate(r.accuseDate)}</span> ·
        responsable {userName(settings, r.responsable)}
      </div>
      <OngletsDossier id="recl" actif={onglet} onChange={setOnglet} items={[
        { k: 'description', label: 'Description' },
        { k: 'reponse', label: 'Réponse' },
        { k: 'actions', label: 'Actions', n: linked.length },
        { k: 'liens', label: 'Liens', n: nbLiens },
      ]} />

      <PanneauDossier id="recl" actif={onglet}>

      {onglet === 'description' && (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.55 }}>{r.description}</p>
      )}

      {onglet === 'actions' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {linked.length === 0
            ? <EmptyState titre="Aucune action corrective rattachée."
                indice={r.statut === 'cloturee'
                  ? 'La réclamation a été clôturée sans action corrective.'
                  : '« Créer une action liée » ouvre une action au plan, rattachée à cette réclamation.'} />
            : <>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Actions liées :</span>
                {/* En panneau, cliquer « AC-… → » fermerait la superposition
                    pour partir dans le plan d'actions : la flèche promettrait
                    un aller-retour qu'on ne saurait pas refaire. On affiche
                    donc la référence, sans promettre un voyage. */}
                {linked.map((a) => (enPanneau
                  ? <span key={a.ref} style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
                  // L'action liée s'ouvre EN PANNEAU : la réclamation qu'on
                  // instruit reste dessous, avec sa réponse en cours de saisie.
                  : <Bouton variante="primaire" taille="sm" key={a.ref} onClick={() => ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: r.ref })} style={{ fontFamily: 'var(--font-mono)' }}>{a.ref} →</Bouton>
                ))}
              </>}
        </div>
      )}

      {/* La réponse au plaignant est le livrable du §7.7 : elle a son onglet,
          en saisie tant que le dossier est ouvert, en lecture ensuite. */}
      {onglet === 'reponse' && (
        peutRedigerReponse && r.statut !== 'cloturee' ? (
          <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: 14, display: 'grid', gap: 8 }}>
            <label style={lbl} htmlFor="recl-rep">Réponse apportée</label>
            <textarea id="recl-rep" value={rep} onChange={(e) => setRep(e.target.value)} rows={4} style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            {/* Le champ existait en base et n'était saisissable NULLE PART. La
                réponse rédigée en interne et le retour effectivement fait au
                réclamant sont deux faits distincts : c'est le second que le
                §7.7 exige, et c'est celui qui manquait. Il bloque désormais la
                clôture, il fallait donc pouvoir l'écrire. */}
            <label style={lbl} htmlFor="recl-retour">Retour fait au réclamant — par qui, quand, comment</label>
            <input id="recl-retour" value={retour} onChange={(e) => setRetour(e.target.value)}
              placeholder="Ex. appelée le 14/04 par Mme Ben Salah, courrier envoyé le 15/04"
              style={inputStyle} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Bouton taille="sm" onClick={() => void enregistrerReponse()} disabled={estOccupe()}>
                {estOccupe('rep') ? 'Enregistrement…' : 'Enregistrer la réponse'}
              </Bouton>
            </div>
          </div>
        ) : r.reponse ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ background: 'var(--green-50)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: 13, color: 'var(--green-600)', lineHeight: 1.55 }}>
              <strong>Réponse :</strong> {r.reponse}
            </div>
            <div style={{ background: r.retourPlaignant ? 'var(--surface-sunken)' : 'var(--red-50)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text-strong)' }}>Retour au réclamant :</strong>{' '}
              {r.retourPlaignant || <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>aucun retour tracé — la personne qui a réclamé ignore la suite donnée.</span>}
            </div>
          </div>
        ) : (
          <EmptyState titre="Aucune réponse enregistrée."
            indice={r.statut === 'cloturee' ? 'La réclamation a été clôturée sans réponse tracée.' : 'Seuls le responsable de la réclamation et le RQ peuvent la rédiger.'} />
        )
      )}

      {onglet === 'liens' && <Liens type="reclamation" refCourante={r.ref} />}

      {/* PLUS D'ONGLET « JOURNAL » (RQ, 24/08/2026) — le bouton dédié, présent
         sur tous les registres, ouvre le même journal en fenêtre. Deux chemins
         pour la même chose obligeaient à savoir lequel prendre, et à les tenir
         tous les deux. Le journal n'est pas une rubrique du dossier : c'est sa
         preuve, elle s'ouvre par-dessus. */}

      </PanneauDossier>

      {/* Les transitions étaient enfermées dans le bloc de réponse : invisibles
          dès qu'on consultait autre chose. Elles appartiennent au dossier, pas
          à une section — d'où la barre persistante. */}
      {/* Dossier SCELLÉ : seule la déclôture reste offerte, et seulement au RQ
          ou au CQ (aligné sur les NC le 08/10/2026 — `peutDecloturerNC`).
          Jusqu'ici la barre disparaissait entièrement à la clôture — un dossier
          clos ne pouvait plus rien, y compris être rouvert quand c'était
          justifié (réclamant qui revient, action jugée insuffisante). */}
      {r.statut === 'cloturee' && peutDecloturerNC(session.roles) && (
        <BarreAction>
          <Decloture recl={r} />
        </BarreAction>
      )}

      {canManage && r.statut !== 'cloturee' && (
        <BarreAction>
          {/* ⚠️ « Passer en analyse » a été RETIRÉ le 26/08/2026, à la demande
              d'Inès : « ce bouton n'a pas beaucoup de sens ». Quand le RQ ouvre
              une réclamation qui vient d'arriver, l'analyse a commencé — le clic
              ne produisait aucune information, seulement des réclamations
              restées « reçues » alors qu'elles étaient traitées.
              La bascule se fait à l'OUVERTURE du dossier (`ged/recl/:id/lu`),
              et seulement pour le RQ et son suppléant. */}
          {/* Émettre exige une réponse ENREGISTRÉE. Le serveur refusait par un
              toast, en laissant l'utilisateur sur l'onglet Description : il
              fallait deviner que le préalable se saisit ailleurs (recette A14).
              On ouvre donc l'onglet où le geste attendu se trouve, au lieu
              d'annoncer un refus sans issue. */}
          {r.statut === 'analyse' && (
            <Bouton variante="primaire" taille="sm" onClick={() => {
              if (!String(r.reponse || '').trim()) { setOnglet('reponse'); return; }
              void transition('reponse', 'Réponse émise au plaignant.');
            }} disabled={estOccupe()}>
              {estOccupe('reponse') ? 'En cours…' : 'Émettre la réponse'}
            </Bouton>
          )}
          {/* Même règle, même bandeau et même dérogation motivée que la NC et
              l'audit. Deux obstacles, tous deux RECALCULÉS à chaque rendu : le
              réclamant non informé, et les actions nées d'ici qui traînent. */}
          {r.statut === 'reponse' && (
            <BlocageCloture
              objet="la réclamation"
              refDossier={r.ref}
              estRQ={session.roles.includes('rq')}
              blocages={[
                ...(!(r.retourPlaignant || '').trim()
                  ? [{ ref: '', libelle: 'le réclamant n’a pas été informé de la suite donnée' }]
                  : []),
                // Le blocage se DIT toujours ; seul le raccourci qui part vers
                // l'action disparaît en panneau — il quitterait la superposition.
                ...linked.filter((a) => AC_BLOQUANTES.includes(a.statut)).map((a) => ({
                  ref: a.ref,
                  libelle: vocabMeta(ACTION_STATES, a.statut).label,
                  onOuvrir: enPanneau ? undefined : () => ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: r.ref }),
                })),
              ]}
              label="Clôturer la réclamation"
              question={`Clôturer ${r.ref} ? L’événement sera signé et le dossier ne pourra plus être rouvert.`}
              aide="Une réclamation ne se clôture pas tant que le réclamant n’a pas été informé de la suite donnée (ISO 15189 §7.7), ni tant qu’une action née d’elle reste à mener. Une réponse rédigée en interne et un retour effectivement fait sont deux faits distincts."
              indiceNonRQ="Tracez le retour fait au réclamant dans l’onglet « Réponse », terminez les actions, ou demandez une dérogation au responsable qualité."
              onCloturer={(motifDerogation, responsable) => smq(
                `ged/recl/${r.documentId}/status`, { statut: 'cloturee', motifDerogation, autorisation: responsable },
                'Réclamation clôturée — événement signé.')} />
          )}
          {!r.actionLiee && (
            <Bouton taille="sm" onClick={() => void run('ac', async () => {
              // L'action s'ouvre pour être formulée : créée seule, elle
              // reprenait l'objet de la réclamation et partait sans échéance.
              const data = await smqData<{ action?: { documentId?: string } }>(
                `ged/recl/${r.documentId}/action`, {}, 'Action corrective liée créée.');
              if (data?.action?.documentId) setAcARemplir(data.action.documentId);
            })} disabled={estOccupe()}>
              <I n="plus" s={13} />{estOccupe('ac') ? 'Création…' : 'Créer une action liée'}
            </Bouton>
          )}
          {/* UNE NC NÉE DE LA RÉCLAMATION (RQ, 13/10/2026) : le formulaire de
              déclaration, prérempli — relevée par l'extérieur, canal
              « réclamation », détectée par le SMQ — et les deux fiches RELIÉES
              par le moteur à la création. */}
          <Bouton taille="sm" onClick={() => setDeclareNC(true)}>
            <I n="plus" s={13} />Déclarer une non-conformité liée
          </Bouton>
        </BarreAction>
      )}

      {declareNC && (
        <Modale titre={`Non-conformité née de ${r.ref}`} largeur={900} onClose={() => setDeclareNC(false)}>
          <DeclareForm encadre={false} reclamationRef={r.ref} onDone={() => setDeclareNC(false)} />
        </Modale>
      )}

      <ActionRedaction documentId={acARemplir} onClose={() => setAcARemplir(null)} />
    </div>
  );
}

/* L8 (11/10/2026) : tri du socle (`useTri`) ; l'ordre métier (en attente d'abord,
   la plus ancienne en tête) reste l'ordre par défaut, dit par `MentionTri`. */
const VALEURS_TRI_RECL: Record<string, (r: GedReclamation) => string> = {
  ref: (r) => r.ref,
  dateReception: (r) => r.dateReception || '',
  statut: (r) => r.statut,
};

export function Reclamations() {
  const { recls, selectedRecl, select, settings, loading, booted, smqFiltre } = useGed();
  const [showNew, setShowNew] = useState(false);
  const [brouillon, setBrouillon] = useState(false);
  const [q, setQ] = useState('');
  // Filtre demandé par le compteur qui nous a amenés ici — lu au montage
  // seulement (`go` l'efface, donc jamais de filtre hérité d'une visite passée).
  const [filtre, setFiltre] = useState(smqFiltre ?? 'toutes');
  const sel = recls.find((r) => r.documentId === selectedRecl) || null;
  const enChargement = !booted || (loading && recls.length === 0);

  const ouvertes = recls.filter(reclOuverte);
  const seuilReponse = useSeuilReponse();
  const tardives = ouvertes.filter((r) => (joursDepuis(r.dateReception) ?? 0) > seuilReponse);
  const cloturees = recls.filter((r) => r.statut === 'cloturee');

  const listeFiltree = useMemo(() => {
    const base = filtre === 'ouvertes' ? ouvertes
      : filtre === 'tardives' ? tardives
        : filtre === 'cloturees' ? cloturees
          : recls;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((r) => `${r.ref} ${r.objet} ${r.plaignant} ${userName(settings, r.responsable)}`.toLowerCase().includes(qq)) : base;
    // Les dossiers ouverts les plus anciens d'abord : ce sont eux qui engagent.
    return [...cherche].sort((a, b) => {
      const oa = a.statut === 'cloturee' ? 1 : 0, ob = b.statut === 'cloturee' ? 1 : 0;
      if (oa !== ob) return oa - ob;
      return (a.dateReception || '').localeCompare(b.dateReception || '');
    });
  }, [recls, filtre, q, settings, ouvertes, tardives, cloturees]);
  const { tri, basculer: onTri, trier } = useTri<GedReclamation>(null, VALEURS_TRI_RECL);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* §7.7 « Réclamations » est la clause de CET écran : recevoir une
          réclamation, l'instruire, y répondre. §8.6 « Amélioration continue »
          est une clause VOISINE et non la même — elle demande de se servir de
          ces retours pour améliorer le dispositif, ce qui se joue au plan
          d'actions et en revue de direction, pas dans le traitement d'une
          réclamation isolée.

          La pastille affichait 8.6 et l'explication ouvrait 7.7 : deux clauses
          différentes pour un même écran, saisies indépendamment et jamais
          comparées. Corrigé le 14/08/2026, avec un contrôle au jeu de tests
          (`qualite-references-iso`) pour que la divergence ne revienne pas. */}
      <TitrePage eyebrow={<>Écoute patient · <RefISO code="7.7" /><Norme art="7.7" /></>} titre="Réclamations"
        actions={
          <Bouton variante="primaire" onClick={() => { setShowNew(true); select('selectedRecl', null); }}>
            <I n="plus" s={15} />Enregistrer une réclamation{!showNew && brouillon ? ' (brouillon conservé)' : ''}
          </Bouton>
        } />

      <NewForm visible={showNew} onDone={() => setShowNew(false)} onDirty={setBrouillon} />

      <div className="ged-2col-wide ged-colonnes-defilantes" style={{ display: 'grid', gridTemplateColumns: '380px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        <div>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher une réclamation…"
            chips={[
              { k: 'toutes', label: 'Toutes', n: recls.length },
              { k: 'ouvertes', label: 'Ouvertes', n: ouvertes.length },
              { k: 'tardives', label: `En cours > ${seuilReponse} j`, n: tardives.length },
              { k: 'cloturees', label: 'Clôturées', n: cloturees.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={recls.length} affiches={listeFiltree.length}
            onReset={() => { setQ(''); setFiltre('toutes'); }} />
          <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <MentionTri>en attente d’abord, puis de la plus ancienne à la plus récente — celle qui attend depuis le plus longtemps passe devant.</MentionTri>
            <span style={{ flex: 1 }} />
            <BoutonsTri label="Trier les réclamations" tri={tri} onTri={onTri}
              options={[{ cle: 'ref', libelle: 'Référence' }, { cle: 'dateReception', libelle: 'Reçue le' }, { cle: 'statut', libelle: 'Statut' }]} />
          </div>
          <div style={{ ...card, overflow: 'hidden' }}>
            {enChargement ? <Skeleton rows={5} height={64} /> : trier(listeFiltree).map((r) => {
              const m = vocabMeta(REC_STATES, r.statut);
              const active = selectedRecl === r.documentId;
              return (
                <LigneLien key={r.documentId} href={cheminDe('recl', r.ref)}
                  title={`Ouvrir ${r.ref}`}
                  onOuvrir={() => { select('selectedRecl', r.documentId); setShowNew(false); }}
                  style={{ width: '100%', textAlign: 'start', background: active ? 'var(--cyan-50)' : 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 14px', fontFamily: 'var(--font-sans)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
                    <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700 }}>{m.label}</span>
                    <Delai r={r} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginTop: 5, lineHeight: 1.35 }}>{r.objet}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{REC_CANAUX.find((c) => c.key === r.canal)?.label} · {r.plaignant || '—'} · {fmtDate(r.dateReception)}</div>
                </LigneLien>
              );
            })}
            {!enChargement && listeFiltree.length === 0 && (
              <EmptyState
                titre={recls.length === 0 ? 'Aucune réclamation enregistrée.' : 'Aucune réclamation ne correspond à ces critères.'}
                indice={recls.length === 0 ? 'L’accusé de réception est tracé automatiquement à l’enregistrement.' : 'Élargissez la recherche ou revenez à « Toutes ».'}
                action={recls.length > 0
                  ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('toutes'); }}>Réinitialiser les filtres</Bouton>
                  : undefined} />
            )}
          </div>
        </div>
        {/* `key` : changer de réclamation remonte la fiche — onglet et
                brouillon de réponse repartent de zéro. */}
        {sel ? <Detail key={sel.documentId} r={sel} /> : (
          <div style={card}>
            <EmptyState titre="Dossier réclamation" indice="Sélectionnez une réclamation dans la liste." />
          </div>
        )}
      </div>
    </div>
  );
}
